import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useCoreApi } from '../api';

type ConnectAccountRequest = {
  pay_pal_account: {
    email: string;
  };
  venmo_account: {
    phone_number: string;
  };
  bank_account: {
    routing_number: string;
    account_number: string;
    bank_account_purpose: string;
    // I'm not sure about these
    // first_name: string;
    // last_name: string;
    // country: string;
    // state: string;
    // address: string;
    // city: string;
    // zip_code: string;
  };
  user: {
    email: string;
    first_name: string;
    last_name: string;
    date_of_birth: string;
    country: string;
    state: string;
    address1: string;
    address2: string;
    city: string;
    zip_code: string;
  };
};
export enum PayoutAccountType {
  PAYPAL = 'paypal',
  VENMO = 'venmo',
  BANK = 'bank',
  HYPERWALLET = 'users',
  NONE = '',
}

type AccountTypes = `${PayoutAccountType}`;
export type ViableAccountsType = Exclude<AccountTypes, '' | 'users'>;
type ConnectAccountArg = {
  accountType: ViableAccountsType | 'users';
  request: Partial<ConnectAccountRequest>;
};

type DeleteAccountArg = {
  accountType: ViableAccountsType;
  uuid: string;
};

enum Translations {
  paypal = 'pay_pal',
  venmo = 'venmo',
  bank = 'bank',
  users = 'users',
}
type ConnectedAccountNames =
  | 'pay_pal_account'
  | 'venmo_account'
  | 'bank_account'
  | 'hyperwallet_user'
  | 'credit_card';

type UseUserConnectedAccounts = {
  [x in ConnectedAccountNames]: boolean;
};

type UseUserConnectedAccountsResponse = {
  accounts: UseUserConnectedAccounts;
};

type AccountCredentials = AccountCredential[];

export interface AccountCredential {
  status: string;
  type: string;
  uuid: string;
  deleted_at: string;
  created_at: string;
  updated_at: string;
  display: string;
  hyperwallet_user: boolean;
  bank: boolean;
  paypal: boolean;
  venmo: boolean;
  bank_account_purpose: string;
  image: string;
}

type AccountCredentialResponse = {
  credentials: AccountCredentials;
};
// Accounts (connected) as boolean AKA connection status
export function useUserConnectedAccounts() {
  const CoreApi = useCoreApi();
  const getAccounts = async () => {
    const { data } = await CoreApi.get<UseUserConnectedAccountsResponse>('/accounts');
    return data.accounts;
  };
  return useQuery({
    queryKey: ['user', 'accounts', 'connected'],
    queryFn: getAccounts,
  });
}

// Accounts info
export function useUserPayoutAccounts<T = AccountCredentials>(
  select?: (data: AccountCredentials) => T
) {
  const CoreApi = useCoreApi();
  const getAccounts = async () => {
    const { data } = await CoreApi.get<AccountCredentialResponse>('/payout_accounts');
    return data.credentials;
  };
  return useQuery({
    queryKey: ['user', 'accounts', 'credentials'],
    queryFn: getAccounts,
    select,
  });
}

export const useSelectPayoutAccount = (selector: ViableAccountsType) => {
  return useUserPayoutAccounts((accounts) =>
    accounts.find((account) => account[`${selector}`] == true)
  );
};

type CreatePayoutAccountResponse = {
  [key in ConnectedAccountNames | 'hyperwallet_users']: AccountCredential;
};
export const useCreatePayoutAccount = () => {
  const CoreApi = useCoreApi();
  const queryClient = useQueryClient();

  const connectAccount = async ({ accountType, request }: ConnectAccountArg) => {
    const { data } = await CoreApi.post<CreatePayoutAccountResponse>(
      `accounts/${Translations[accountType]}`,
      request
    );

    return data;
  };

  return useMutation({
    mutationFn: connectAccount,
    onSuccess(data) {
      if (data.hyperwallet_users) {
        queryClient.setQueryData(
          ['user', 'accounts', 'connected'],
          (prev: UseUserConnectedAccounts | {} = {}) => ({
            ...prev,
            hyperwallet_user: true,
          })
        );
      } else {
        const [key, value] = Object.entries(data)[0];
        queryClient.setQueryData(
          ['user', 'accounts', 'connected'],
          (prev: UseUserConnectedAccounts | {} = {}) => ({
            ...prev,
            [key]: true,
          })
        );
        queryClient.setQueryData(
          ['user', 'accounts', 'credentials'],
          (prev: AccountCredentials = []) => [...prev, { ...value }]
        );
      }
    },
  });
};

export const useDeletePayoutAccount = () => {
  const CoreApi = useCoreApi();
  const queryClient = useQueryClient();

  const deleteAccount = async ({ accountType, uuid }: DeleteAccountArg) => {
    const { data } = await CoreApi.delete(`accounts/${Translations[accountType]}/${uuid}`);
    return data;
  };

  return useMutation({
    mutationFn: deleteAccount,
    onSuccess() {
      return queryClient.invalidateQueries({
        queryKey: ['user', 'accounts']
      });
    },
  });
};
